import { getAccountAttribution } from "~/utils/account-engagement.client";

export type DoctorFunnelEvent =
  | "doctor_profile_viewed"
  | "service_selected"
  | "slot_selected"
  | "booking_started"
  | "payment_started";

interface DoctorAnalyticsContext {
  doctorId: string;
  profileId?: string;
  doctorSlug?: string;
  bookingType?: string;
  partnerSlug?: string;
  language?: string;
  serviceId?: string;
  journeyId?: string;
  originPlatform?: "website" | "patient_mobile" | "whatsapp" | "kiko" | "other";
  eventPlatform?: "website" | "patient_mobile" | "whatsapp" | "kiko" | "other";
  entryPoint?: string;
  appointmentId?: string;
}

declare global {
  interface Window {
    clarity?: (
      command: "event" | "set",
      name: string,
      value?: string
    ) => void;
  }
}

const SESSION_KEY = "laruche_doctor_analytics_session";
const JOURNEY_KEY_PREFIX = "laruche_doctor_booking_journey:";
const JOURNEY_ACTIVITY_WINDOW_MS = 30 * 60 * 1000;

interface ActiveJourney {
  id: string;
  lastActiveAt: number;
  completedAt?: number;
}

const createId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getSessionId = () => {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const sessionId = createId();
    window.sessionStorage.setItem(SESSION_KEY, sessionId);
    return sessionId;
  } catch {
    return createId();
  }
};

const getJourneyId = (doctorId: string) => {
  try {
    const key = `${JOURNEY_KEY_PREFIX}${doctorId}`;
    const raw = window.sessionStorage.getItem(key);
    const existing = raw ? (JSON.parse(raw) as ActiveJourney) : undefined;
    const now = Date.now();
    if (
      existing?.id &&
      !existing.completedAt &&
      now - existing.lastActiveAt <= JOURNEY_ACTIVITY_WINDOW_MS
    ) {
      window.sessionStorage.setItem(
        key,
        JSON.stringify({ ...existing, lastActiveAt: now })
      );
      return existing.id;
    }
    const journey: ActiveJourney = { id: createId(), lastActiveAt: now };
    window.sessionStorage.setItem(key, JSON.stringify(journey));
    return journey.id;
  } catch {
    return createId();
  }
};

export const completeDoctorAnalyticsJourney = (doctorId: string) => {
  if (typeof window === "undefined" || !doctorId) return;
  try {
    const key = `${JOURNEY_KEY_PREFIX}${doctorId}`;
    const raw = window.sessionStorage.getItem(key);
    const current = raw ? (JSON.parse(raw) as ActiveJourney) : undefined;
    if (current?.id) {
      window.sessionStorage.setItem(
        key,
        JSON.stringify({ ...current, lastActiveAt: Date.now(), completedAt: Date.now() })
      );
    }
  } catch {
    // Journey lifecycle must never interrupt appointment booking.
  }
};

export const appendDoctorAnalyticsAttribution = (
  formData: FormData,
  context: DoctorAnalyticsContext
) => {
  if (typeof window === "undefined" || !context.doctorId) return;
  formData.set(
    "analytics_journey_id",
    context.journeyId || getJourneyId(context.doctorId)
  );
  formData.set("analytics_origin_platform", context.originPlatform || "website");
  formData.set("analytics_entry_point", context.entryPoint || "doctor_profile");
  if (context.profileId) formData.set("analytics_profile_id", context.profileId);
  if (context.serviceId) formData.set("analytics_service_id", context.serviceId);
};

export const trackDoctorFunnelEvent = (
  event: DoctorFunnelEvent,
  context: DoctorAnalyticsContext
) => {
  if (typeof window === "undefined" || !context.doctorId) return;

  window.clarity?.("set", "doctor_profile_id", context.profileId ?? "");
  window.clarity?.("set", "doctor_id", context.doctorId);
  window.clarity?.("set", "booking_type", context.bookingType ?? "unknown");
  if (context.partnerSlug) {
    window.clarity?.("set", "partner", context.partnerSlug);
  }
  window.clarity?.("event", event);
  const attribution = getAccountAttribution(context.doctorSlug);
  const lastTouch = attribution.lastTouch || {};

  void fetch("/api/doctor-profile-event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      event_id: createId(),
      event,
      doctor_id: context.doctorId,
      profile_id: context.profileId,
      doctor_slug: context.doctorSlug,
      session_id: getSessionId(),
      journey_id: context.journeyId || getJourneyId(context.doctorId),
      origin_platform: context.originPlatform || "website",
      event_platform: context.eventPlatform || "website",
      entry_point: context.entryPoint || "doctor_profile",
      booking_type: context.bookingType,
      service_id: context.serviceId,
      appointment_id: context.appointmentId,
      attribution_share_id: attribution.shareId,
      attribution_channel: attribution.channel,
      analytics_source: lastTouch.source,
      analytics_medium: lastTouch.medium,
      analytics_campaign: lastTouch.campaign,
      analytics_referrer_domain: lastTouch.referrer_domain,
      partner_slug: context.partnerSlug,
      language: context.language,
      schema_version: 2,
    }),
  }).catch(() => {
    // Analytics must never interrupt appointment booking.
  });
};
